// =============================================
// Step3PlanTable — krok 3: plan per domena (3 kolumny run / skip z powodem),
// podsumowanie, "Wykonaj" → NsConfirmDialog (liczba domen z przepisywanymi NS,
// pominiete per kategoria, inne akcje).
// =============================================
import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Play, Copy } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import type { TransferWizardState } from "@/hooks/useTransferWizard";
import { PlannedActionCell } from "./PlanRowStatusCell";
import { NsConfirmDialog, type NsConfirmRow } from "./NsConfirmDialog";
import { planCounts, otherActionsLabel } from "./planSummary";

export const Step3PlanTable = ({ wizard }: { wizard: TransferWizardState }) => {
  const { toast } = useToast();
  const { plan, startRun, canNext3 } = wizard;
  const [confirmOpen, setConfirmOpen] = useState(false);
  const counts = useMemo(() => planCounts(plan.rows), [plan]);

  const nsRows: NsConfirmRow[] = plan.rows
    .filter((r) => r.willRewriteNs)
    .map((r) => {
      const a = r.actions[1];
      return { domain: r.domain.key, currentNs: a.kind === "ns_set" && a.decision === "run" ? a.currentNs : [], newNs: a.kind === "ns_set" && a.decision === "run" ? a.ns : [] };
    });

  const copyManual = (ns: string[]) => { navigator.clipboard.writeText(ns.join("\n")); toast({ title: "Skopiowano NS", description: ns.join(", ") }); };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <Badge variant="outline">{plan.rows.length} domen</Badge>
        <Badge variant="outline">zone_create: {counts.zone}</Badge>
        <Badge variant="outline" className={counts.ns ? "border-amber-500/40 text-amber-400" : ""}>ns_set: {counts.ns}{counts.ns ? " — NS zostaną PRZEPISANE" : ""}</Badge>
        <Badge variant="outline">a_record: {counts.a}</Badge>
        <span className="text-xs text-muted-foreground">· {plan.rows.length - plan.runnableCount} domen bez żadnej akcji</span>
        <Button className="ml-auto" onClick={() => setConfirmOpen(true)} disabled={!canNext3}>
          <Play className="h-4 w-4 mr-2" />Wykonaj
        </Button>
      </div>

      <div className="rounded-lg border overflow-x-auto max-h-[60vh] overflow-y-auto">
        <Table>
          <TableHeader className="sticky top-0 bg-background z-10">
            <TableRow>
              <TableHead>Domena</TableHead>
              <TableHead>1 · Zona w CF</TableHead>
              <TableHead>2 · NS w AfterMarket</TableHead>
              <TableHead>3 · Rekordy A</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {plan.rows.map((r) => {
              const allSkip = r.actions.every((a) => a.decision === "skip");
              const ns = r.actions[1];
              const manual = ns.kind === "ns_set" && ns.decision === "skip" && ns.manualNs && ns.manualNs !== "FROM_CREATED_ZONE" ? ns.manualNs : null;
              return (
                <TableRow key={r.domain.key} className={allSkip ? "opacity-50" : ""}>
                  <TableCell className="font-mono text-sm align-top">
                    {r.domain.key}
                    {r.alreadyCloudflare && !r.existingZone && <Badge variant="outline" className="ml-2 text-[10px] px-1.5 py-0 border-amber-500/40 text-amber-400">CF spoza panelu</Badge>}
                    {r.existingZone && <div className="text-[11px] text-muted-foreground font-sans">zona: {r.existingZone.cfAccountLabel} · {r.existingZone.status}</div>}
                  </TableCell>
                  <TableCell className="align-top"><PlannedActionCell action={r.actions[0]} /></TableCell>
                  <TableCell className="align-top">
                    <PlannedActionCell action={r.actions[1]} />
                    {manual && (
                      <Button variant="ghost" size="sm" className="h-6 px-2 mt-1 text-[11px]" onClick={() => copyManual(manual)}><Copy className="h-3 w-3 mr-1" />Kopiuj NS do ręcznego ustawienia</Button>
                    )}
                  </TableCell>
                  <TableCell className="align-top"><PlannedActionCell action={r.actions[2]} /></TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <NsConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        onConfirm={() => { setConfirmOpen(false); void startRun(); }}
        rows={nsRows}
        skipSummary={plan.skipSummary}
        otherActionsLabel={otherActionsLabel(counts)}
      />
    </div>
  );
};
